import { useState, useEffect, useRef, ChangeEvent, Dispatch, SetStateAction } from 'react';
import axios, { AxiosResponse } from 'axios';
import { getCounties } from '@/services/address.services';
import { useValid } from '@/contexts/TenantContext/TenantContext';
import { opcoSettings } from '../../../../../../config/opcoSettings';

export interface EditMicrosoftDataFormProps {
  isOpen: boolean;
  setIsOpen: (data: boolean) => void;
  onClose: () => void;
  onSubmit: (data: Record<string, string>) => void;
  onErrors: (newErrors: Record<string, boolean>) => void;
  setMicrosoftAdditionalData: (data: string) => void;
  continueBtn: Dispatch<SetStateAction<boolean>>;
  accountId: string;
  intentClose: boolean;
  needsMCA?: boolean;
  needsAdditionalInfo?: boolean;
  onMcaVerified?: (verified: boolean) => void;
}

interface County {
  name: string;
}

const OPCO = process.env.GATSBY_OPCO;
type GetCountiesResponse = County[];

export const useEditMicrosoftDataForm = ({
  isOpen,
  onClose,
  onSubmit,
  onErrors,
  setMicrosoftAdditionalData,
  continueBtn,
  accountId,
  needsMCA,
  needsAdditionalInfo,
}: EditMicrosoftDataFormProps) => {
  const [counties, setCounties] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  //add everything from context
  const {
    setTenantName,
    isTenantValid,
    setPrincipleName,
    isPrincipleNameValid,
    setEmail,
    isEmailValid,
    setFirstName,
    isFirstNameValid,
    setLastName,
    isLastNameValid,
    setBusinessName,
    isBusinessNameValid,
    setAddress,
    isAddressValid,
    setAddress2,
    isAddress2Valid,
    setZipCode,
    isZipCodeValid,
    setCity,
    isCityValid,
    setCountry,
    isCountryValid,
    setPhone,
    isPhoneValid,
    setOrgNumber,
    isOrgNumberValid,
    setState,
    isStateValid,
    setValidForm,
    setAltEmail,
    isAltEmailValid,
  } = useValid();

  //getting county
  useEffect(() => {
    const fetchCounties = async () => {
      try {
        const response = await getCounties(OPCO || 'IE');

        if ((response as AxiosResponse).data) {
          const countyNames = (response as AxiosResponse<GetCountiesResponse>).data.map(
            (county: { name: string }) => county.name
          );
          setCounties(countyNames);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        if (axios.isAxiosError(error)) {
          console.error('Error fetching counties:', error.message);
        } else {
          console.error('An unexpected error occurred:', error);
        }
      }
    };

    fetchCounties();
  }, []);

  const [optionalData, setOptionalData] = useState({
    address2: '',
  });

  const sanitizeInput = (value: string) => {
    return value.replace(/[<>/{}]/g, '');
  };

  const handleOptionalData = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    const sanitizedValue = sanitizeInput(value);
    setOptionalData(prev => {
      const updatedData = { ...prev, [name]: sanitizedValue };
      localStorage.setItem(`${accountId}_optionalData`, JSON.stringify(updatedData)); // Save every change
      return updatedData;
    });
  };

  const [formData, setFormData] = useState({
    tenantName: '',
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    altEmail: '',
    zipCode: '',
    country: '',
    address: '',
    phone: '',
    businessName: '',
    county: '',
    city: '',
    registrationNumber: '',
    consentFirstName: '',
    consentLastName: '',
    consentEmail: '',
    consentPhone: '',
  });

  const [errors, setErrors] = useState<Record<string, boolean>>({});

  //getting data and state stored in localstorage

  useEffect(() => {
    if (!isOpen) return;

    // Step 1: Check if currentAccountId is already defined in localStorage
    let prevAccountId = localStorage.getItem('currentAccountId');

    if (!prevAccountId) {
      // Set a new accountId
      prevAccountId = 'default';
      localStorage.setItem('currentAccountId', prevAccountId);
    }

    // Step 2: If accountId is different, clean up old data and update
    if (prevAccountId !== accountId) {
      if (prevAccountId) {
        // Clean up previous account data in localStorage
        localStorage.removeItem(`${prevAccountId}_formData`);
        localStorage.removeItem(`${prevAccountId}_formErrors`);
        localStorage.removeItem(`${prevAccountId}_optionalData`);
        localStorage.removeItem(`${prevAccountId}_microsoftAdditionalData`);
      }
      // Set the current account ID to localStorage
      localStorage.setItem('currentAccountId', accountId);
    }

    // Step 3: Fetch and update data for the current account
    const savedData = localStorage.getItem(`${accountId}_formData`);
    const savedErrors = localStorage.getItem(`${accountId}_formErrors`);
    const optionalDataLocal = localStorage.getItem(`${accountId}_optionalData`);

    // Step 4: Update state if data is found
    try {
      if (savedData) {
        setFormData(JSON.parse(savedData));
      }
      if (savedErrors) {
        setErrors(JSON.parse(savedErrors));
      }
      if (optionalDataLocal) {
        setOptionalData(JSON.parse(optionalDataLocal));
      }
    } catch (error) {
      console.error('Error parsing data from localStorage:', error);
    }
  }, [isOpen, accountId]);

  //getting user input after sanitization and set them in localstorage
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const getInputRef = (name: string) => (el: HTMLInputElement | null) => {
    inputRefs.current[name] = el;
  };
  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const { name, value } = event.target;

    // Sanitize input
    const sanitizedValue = sanitizeInput(value);

    // Validate firstName and lastName
    if (name === 'firstName' || name === 'lastName') {
      const nameRegex = /^[A-Za-z\s]*$/; // Allows only letters and spaces
      if (!nameRegex.test(sanitizedValue)) {
        setErrors(prev => ({
          ...prev,
          [name]: true, // Set error for invalid input
        }));
        return; // Reject invalid input
      }
    }

    // Update state
    setFormData(prev => {
      const updatedData = { ...prev, [name]: sanitizedValue };
      localStorage.setItem(`${accountId}_formData`, JSON.stringify(updatedData)); // Save every change
      return updatedData;
    });

    // Clear error if input is valid
    setErrors(prev => ({
      ...prev,
      [name]: false, // Clear error for valid input
    }));

    setTimeout(() => {
      inputRefs.current[name]?.focus();
    }, 0);
  };

  const validateForm = () => {
    const newErrors: Record<string, boolean> = {};
    Object.keys(formData).forEach(field => {
      const value = (formData[field as keyof typeof formData] || '').trim();

      if (!value) {
        newErrors[field] = true;
      }
    });

    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const microsoftEmailRegex = /@microsoft\.com$/i; // Matches "@Microsoft.com" case-insensitively
    if (formData.email) {
      // Validate email: invalid format or rejected Microsoft domains
      if (!emailRegex.test(formData.email) || microsoftEmailRegex.test(formData.email)) {
        newErrors.email = true;
      }
    }

    // Phone validation: only numbers and optional length check
    const phoneRegex = opcoSettings[OPCO as keyof typeof opcoSettings].phoneRegex;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = true;
    }

    //zip code of only 7 characters
    const zipCodeRegex = /^[a-zA-Z0-9]{7}$/;
    if (formData.zipCode && !zipCodeRegex.test(formData.zipCode)) {
      newErrors.zipCode = true;
    }
    return newErrors;
  };

  const updateContext = () => {
    setTenantName(formData.tenantName);
    setAddress(formData.address);
    setAddress2(optionalData.address2);
    setBusinessName(formData.businessName);
    setCity(formData.city);
    setCountry(formData.country);
    setEmail(formData.email);
    setAltEmail(formData.altEmail);
    setFirstName(formData.firstName);
    setLastName(formData.lastName);
    setPhone(formData.phone);
    setZipCode(formData.zipCode);
    setState(formData.county);
    setPrincipleName(formData.username);
    setOrgNumber(formData.registrationNumber);
  };

  const handleSubmit = () => {
    const shouldValidateAdditionalInfo = needsAdditionalInfo ?? true;

    if (shouldValidateAdditionalInfo) {
      updateContext();
    }

    const newErrors = shouldValidateAdditionalInfo ? validateForm() : {};

    window.utag?.view({
      event_name: 'ui_interaction',
      component_name: 'Active_Overlay',
      event_category: 'Additional Info Overlay',
      event_label: 'Save Details',
      event_action: 'Additional Info Overlay',
      compoennt_type: 'Button',
    });

    if (Object.keys(newErrors).length === 0) {
      onClose();
      localStorage.setItem(`${accountId}_microsoftAdditionalData`, 'success');
      setMicrosoftAdditionalData('success');
      onSubmit(formData);
      localStorage.removeItem(`${accountId}_formErrors`);
      continueBtn(false);
      onErrors({});
      setValidForm(true);

      window.utag?.view({
        event_name: 'ui_interaction',
        component_name: 'CLick Component',
        event_category: 'Basket',
        event_label: 'Edit',
        event_action: 'Complete Information',
        compoennt_type: 'Button',
      });
    } else {
      onClose();
      setErrors(newErrors);
      onErrors(newErrors);
      localStorage.setItem(`${accountId}_formErrors`, JSON.stringify(newErrors));
      localStorage.setItem(`${accountId}_microsoftAdditionalData`, 'error');
      continueBtn(true);
      setMicrosoftAdditionalData('error');

      window.utag?.view({
        event_name: 'ui_interaction',
        component_name: 'CLick Component',
        event_category: 'Basket',
        event_label: 'Edit',
        event_action: 'Missing Information',
        compoennt_type: 'Button',
      });
    }
  };

  return {
    counties,
    loading,
    formData,
    optionalData,
    errors,
    handleOptionalData,
    handleChange,
    handleSubmit,
    getInputRef,
    isTenantValid,
    isPrincipleNameValid,
    isEmailValid,
    isAltEmailValid,
    isFirstNameValid,
    isLastNameValid,
    isBusinessNameValid,
    isAddressValid,
    isAddress2Valid,
    isZipCodeValid,
    isCityValid,
    isCountryValid,
    isStateValid,
    isPhoneValid,
    isOrgNumberValid,
  };
};
